import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const INPUT = "w-full border-2 border-black px-2 py-1.5 text-sm bg-white";
const LABEL = "text-[10px] uppercase tracking-widest font-bold";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function PhotoField({ photo, setPhoto, testId }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/testimonials/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3" data-testid={`${testId}-photo`}>
      {photo?.url
        ? <img src={`${BACKEND}${photo.url}`} alt="" className="h-12 w-12 rounded-full border-2 border-black object-cover" data-testid={`${testId}-photo-preview`} />
        : <div className="h-12 w-12 rounded-full border-2 border-dashed border-zinc-400" />}
      <label className="border-2 border-black bg-white px-2.5 py-1 text-[10px] uppercase tracking-widest cursor-pointer hover:bg-zinc-100">
        {busy ? "Uploading…" : photo ? "Change photo" : "Add client photo (optional)"}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy}
               onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testId}-photo-input`} />
      </label>
      {photo && <button type="button" onClick={() => setPhoto(null)} className="text-[10px] uppercase tracking-widest underline" data-testid={`${testId}-photo-remove`}>Remove</button>}
    </div>
  );
}

export function TestimonialForm({ pageId, initial, admin, onSaved, onCancel, testId = "testimonial-form" }) {
  const [f, setF] = useState({
    name: initial?.name || "", case_tag: initial?.case_tag || "", quote: initial?.quote || "",
    stars: initial?.stars || 5, consent: Boolean(initial),
  });
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState(initial?.photo_file_id ? { file_id: initial.photo_file_id, url: initial.photo_url } : null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, stars: Number(f.stars), case_tag: f.case_tag.trim() || null, photo_file_id: photo?.file_id || null };
    try {
      const { data } = initial
        ? await api.patch(`/testimonials/${initial.id}`, body)
        : await api.post("/testimonials", { ...body, page_id: pageId });
      toast.success(admin ? "Testimonial saved" : "Sent for approval — it goes live once an admin approves it");
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border-2 border-black bg-zinc-50 p-3 flex flex-col gap-2.5" data-testid={testId}>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_90px] gap-2">
        <label className="flex flex-col gap-1"><span className={LABEL}>Client name</span>
          <input className={INPUT} value={f.name} onChange={set("name")} maxLength={60} required placeholder="Maria G." data-testid={`${testId}-name`} />
        </label>
        <label className="flex flex-col gap-1"><span className={LABEL}>Case type (optional)</span>
          <input className={INPUT} value={f.case_tag} onChange={set("case_tag")} maxLength={60} placeholder="Car accident · Atlanta, GA" data-testid={`${testId}-case`} />
        </label>
        <label className="flex flex-col gap-1"><span className={LABEL}>Stars</span>
          <select className={INPUT} value={f.stars} onChange={set("stars")} data-testid={`${testId}-stars`}>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
          </select>
        </label>
      </div>
      <label className="flex flex-col gap-1"><span className={LABEL}>What the client said</span>
        <textarea className={`${INPUT} min-h-[84px]`} value={f.quote} onChange={set("quote")} minLength={20} maxLength={600} required data-testid={`${testId}-quote`} />
        <span className="text-[10px] text-zinc-500">{f.quote.length}/600 · at least 20 characters</span>
      </label>
      <PhotoField photo={photo} setPhoto={setPhoto} testId={testId} />
      <label className="flex items-start gap-2 text-xs">
        <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5" data-testid={`${testId}-consent`} />
        <span>This is a real client who agreed to have these words shown on our pages.</span>
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={saving || !f.consent} data-testid={`${testId}-save`}
                className="border-2 border-black bg-black text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
          {saving ? "Saving…" : admin ? "Save" : "Send for approval"}
        </button>
        <button type="button" onClick={onCancel} data-testid={`${testId}-cancel`}
                className="border-2 border-black bg-white px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-zinc-100">
          Cancel
        </button>
      </div>
    </form>
  );
}
